import React, { useState } from 'react';
import { stageAPI } from '../services/api';
import { dateFr } from '../services/dates';
import StageModale from './StageModale';
import { MenuFormations, MenuFormationsCertif, nomPropose, prefixeFormations, dansLOrdre } from './StageFormationsProcessus';

/**
 * SUIVI DE STAGE — l'ACCUEIL À VIGNETTES (lot L1a, écart E169)
 * ============================================================
 * Spec     : doc/modules/suivi_de_stage.md « Navigation — accueil à vignettes », E169
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#tab-accueil)
 *
 * Trois lignes : Outils généraux · Suivi stagiaires / alternants (une vignette = un processus de suivi) ·
 * Certification des documents (une vignette = un processus de certification). Les données viennent d'UNE lecture
 * (`/stage/accueil`, faite par la coquille) : chaque vignette dit ce qui attend un geste et OÙ elle s'ouvrira
 * (règle d'ouverture : « Le processus » tant que « ce qui reste à faire » n'est pas vide, sinon le tableau de bord).
 *
 * Les créations (« Nouveau processus… ») et les reprises (« Récupérer N-1 », « Reprendre l'année précédente »)
 * appellent les MÊMES routes que les écrans de paramétrage : rien de nouveau côté serveur. Dans une vignette,
 * ces boutons n'existent plus (E169).
 *
 * L3a (E171) : un processus suit PLUSIEURS formations ; la vignette porte son nom (enregistré, sinon calculé :
 * « BUT1 FI — Stage », « BUT2 — Stage » ; « … — Stage S2 » pour un processus d'avant la v90, E176) et une puce par formation.
 */

/**
 * Lot L1c — ce que chaque compte retient de SON accueil : Cartes / Liste et repli par ligne, « Masquer les terminés ».
 * ⚠️ Dans le navigateur, avec une clé PAR COMPTE (convention du module : visibilité du tableau de bord, import des
 * apprentis). Une mémoire illisible (navigation privée, stockage bloqué) ne bloque rien : on retombe sur le défaut.
 */
function memoire(userId) {
    const cle = (k) => `stage.accueil.${k}.${userId || 'anonyme'}`;
    return {
        lire: (k, defaut) => { try { const v = localStorage.getItem(cle(k)); return v === null ? defaut : JSON.parse(v); } catch (_) { return defaut; } },
        ecrire: (k, v) => { try { localStorage.setItem(cle(k), JSON.stringify(v)); } catch (_) { /* sans mémoire : sans effet */ } }
    };
}
/** Au-delà de ce nombre de vignettes dans une ligne, elles passent en COMPACT (compteurs sur une ligne). */
const SEUIL_COMPACT = 4;

/** En-tête d'une ligne : la flèche de repli, le titre, le nombre, « Cartes / Liste » ; le reste à droite. */
function EnteteLigne({ titre, icone, couleur, n, vue, onVue, replie, onReplier, children }) {
    return (
        <div className="acc-ligne-h">
            <button type="button" className="acc-repli" title={replie ? 'Déplier la ligne' : 'Replier la ligne'} aria-expanded={!replie} onClick={onReplier}>
                <i className="fa-solid fa-chevron-down" />
            </button>
            <span className="acc-ligne-titre"><i className={'fa-solid ' + icone} style={{ color: couleur }} /> {titre}</span>
            <span className="acc-ligne-n">{n}</span>
            <span className="acc-bascule" role="group" aria-label="Affichage de la ligne">
                <button type="button" className={vue === 'cartes' ? 'on' : ''} aria-pressed={vue === 'cartes'} onClick={() => onVue('cartes')}><i className="fa-solid fa-grip" /> Cartes</button>
                <button type="button" className={vue === 'liste' ? 'on' : ''} aria-pressed={vue === 'liste'} title="Une ligne par processus" onClick={() => onVue('liste')}><i className="fa-solid fa-list" /> Liste</button>
            </span>
            {children}
        </div>
    );
}
/** Les classes d'une ligne de vignettes : liste, ou compact au-delà du seuil. */
const classeVignettes = (vue, n) => 'acc-vignettes' + (vue === 'liste' ? ' liste' : n > SEUIL_COMPACT ? ' dense' : '');

const ETAT_SUIVI = { a_lancer: 'À lancer', en_cours: 'En cours', termine: 'Terminé' };
const ETAT_CERTIF = { non_lance: 'Prêt, non lancé', lance: 'Lancé (figé)', termine: 'Terminé' };
const jjmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

function Mini({ valeur, libelle, ton = '', titre }) {
    return (
        <div className={'acc-mini ' + ton} title={titre}>
            <b>{valeur === null || valeur === undefined ? '—' : valeur}</b>
            <span>{libelle}</span>
        </div>
    );
}

function PiedEtat({ v, libelleEtat }) {
    const manque = v.reste.filter((r) => !r.ok).length;
    return (
        <div className="acc-vg-pied">
            {v.pret
                ? <span className={'acc-chip ' + (v.etat === 'termine' ? '' : 'ok')}>
                      {v.etat === 'lance' && <i className="fa-solid fa-lock" />} {libelleEtat}
                  </span>
                : <span className="acc-chip afaire"><i className="fa-solid fa-gear" /> Paramétrage à terminer ({manque})</span>}
            <span className="acc-vg-ouvre">s'ouvre sur {v.pret ? 'le tableau de bord' : '« Le processus »'}</span>
        </div>
    );
}

/**
 * E177 (maquette rév. 14, 22/10) — les actions d'une vignette, TOUJOURS visibles (en haut à droite en cartes, en bout de
 * ligne en liste) : la corbeille, seulement pour un processus dont la campagne n'est PAS lancée (le serveur le refuse
 * sinon). ⚠️ Le clic ne doit pas OUVRIR la vignette (stopPropagation, clavier compris).
 */
function ActionsVignette({ supprimable, modifiable, titre, onSupprimer }) {
    if (!supprimable || !modifiable) return null;
    const stop = (e) => e.stopPropagation();
    return (
        <div className="acc-vg-act" onClick={stop} onKeyDown={stop}>
            <button type="button" className="suppr" title={titre} aria-label={titre} onClick={onSupprimer}>
                <i className="fa-solid fa-trash" />
            </button>
        </div>
    );
}

function VignetteSuivi({ v, onOuvrir, modifiable, onSupprimer }) {
    const c = v.compteurs;
    const exclure = c.a_exclure > 0;
    return (
        <div className="acc-vg" role="button" tabIndex={0} onClick={() => onOuvrir(v)} onKeyDown={(e) => { if (e.key === 'Enter') onOuvrir(v); }}>
            {/* « À lancer » = aucun dossier n'a avancé : la règle même de la suppression côté serveur. */}
            <ActionsVignette supprimable={v.etat === 'a_lancer'} modifiable={modifiable}
                             titre="Supprimer ce processus (campagne pas lancée)" onSupprimer={() => onSupprimer(v)} />
            <div className="acc-vg-h">
                <div className="acc-vg-ic"><i className="fa-solid fa-diagram-project" /></div>
                <div className="acc-vg-tx">
                    <div className="acc-vg-t">{v.titre}</div>
                    <div className="acc-vg-sous">
                        {v.date_debut && v.date_fin ? `${jjmm(v.date_debut)} → ${dateFr(v.date_fin)}` : 'dates à saisir'}
                        {!v.visite ? ' · sans visite' : ''}
                    </div>
                    {/* L3a : une puce par formation ; avec plusieurs, chacune dit ses dossiers (le total est dans « dossiers »). */}
                    <div className="acc-vg-form">{v.formations.map((f) => (
                        <span key={f.id} className="acc-chip">{f.nom}{v.formations.length > 1 ? ` · ${f.nb_dossiers}` : ''}</span>
                    ))}</div>
                </div>
            </div>
            <div className="acc-vg-chiffres">
                {/* E179 : les dossiers ACTIFS (= le tableau de bord à l'ouverture, « Actifs » coché) ; le reste au survol. */}
                <Mini valeur={c.dossiers} libelle="dossiers" titre={`Dossiers actifs (les validés ont leur chiffre à droite)${c.non_comptes ? ` — ${c.non_comptes} sorti${c.non_comptes > 1 ? 's' : ''} du suivi (abandon, étranger, alternance) non compté${c.non_comptes > 1 ? 's' : ''}` : ''}`} />
                <Mini valeur={c.a_valider} libelle="à valider" ton={c.a_valider ? 'or' : ''} />
                {exclure
                    ? <Mini valeur={c.a_exclure} libelle="à exclure" ton="rouge" />
                    : <Mini valeur={c.a_relancer} libelle="à relancer" ton={c.a_relancer ? 'rouge' : ''} />}
                <Mini valeur={c.valides} libelle="validés" ton="v" />
            </div>
            <PiedEtat v={v} libelleEtat={ETAT_SUIVI[v.etat]} />
        </div>
    );
}

function VignetteCertif({ v, onOuvrir, modifiable, onSupprimer }) {
    const c = v.compteurs;
    const lance = v.etat !== 'non_lance';
    return (
        <div className={'acc-vg certif' + (v.etat === 'termine' ? ' termine' : '')} role="button" tabIndex={0} onClick={() => onOuvrir(v)} onKeyDown={(e) => { if (e.key === 'Enter') onOuvrir(v); }}>
            <ActionsVignette supprimable={!lance} modifiable={modifiable}
                             titre="Supprimer ce processus (jamais lancé)" onSupprimer={() => onSupprimer(v)} />
            <div className="acc-vg-h">
                <div className="acc-vg-ic"><i className="fa-solid fa-stamp" /></div>
                <div className="acc-vg-tx">
                    <div className="acc-vg-t">{v.titre}</div>
                    <div className="acc-vg-sous">
                        {v.periode_debut && v.periode_fin ? `${dateFr(v.periode_debut)} → ${dateFr(v.periode_fin)}` : 'dates à saisir'}
                        {v.date_limite_certification ? ` · avant le ${dateFr(v.date_limite_certification)}` : ''}
                    </div>
                </div>
            </div>
            <div className="acc-vg-chiffres">
                {/* Avant le lancement, il n'y a pas de dossier : on montre les inscrits de la formation. */}
                {lance ? <Mini valeur={c.dossiers} libelle="dossiers" /> : <Mini valeur={c.inscrits} libelle="inscrits" />}
                <Mini valeur={lance ? c.a_traiter : null} libelle="à traiter" ton={c.a_traiter ? 'or' : ''} />
                <Mini valeur={lance ? c.sans_certificateur : null} libelle="sans certif." ton={c.sans_certificateur ? 'rouge' : ''} />
                <Mini valeur={lance ? c.certifies : null} libelle="certifiés" ton="v" />
            </div>
            <PiedEtat v={v} libelleEtat={ETAT_CERTIF[v.etat]} />
        </div>
    );
}

function Outil({ couleur, icone, titre, sous, chip, chipTon = '', onClick }) {
    return (
        <div className="acc-vg outil" style={{ '--c': couleur }} role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
            <div className="acc-vg-h">
                <div className="acc-vg-ic"><i className={'fa-solid ' + icone} /></div>
                <div className="acc-vg-tx">
                    <div className="acc-vg-t">{titre}</div>
                    <div className="acc-vg-sous">{sous}</div>
                </div>
            </div>
            <div className="acc-vg-pied">{chip && <span className={'acc-chip ' + chipTon}>{chip}</span>}</div>
        </div>
    );
}

/**
 * « Nouveau processus de suivi » : les formations (plusieurs, menu à cases) et le nom de la vignette — le reste se
 * règle dans « Le processus ». L3a (E171) : le nom est PROPOSÉ (« BUT2 — Stage ») et modifiable ; laissé tel quel, il
 * n'est pas enregistré et suit les formations du processus.
 * E176 (v90) : PLUS DE SEMESTRE (maquette rév. 13) — rien à créer dans le Programme de formation ; une formation déjà
 * suivie ailleurs se coche quand même (Q1), le menu le dit.
 */
function NouveauSuivi({ departementId, anneeId, onCree, onFermer }) {
    const [donnees, setDonnees] = useState(null);
    const [coches, setCoches] = useState([]);
    const [nom, setNom] = useState(null);   // null = le nom proposé
    const [copierDe, setCopierDe] = useState('');   // L2a (E170) : '' = les modèles d'origine du département
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    React.useEffect(() => {
        stageAPI.getParametrage(departementId, anneeId)
            .then((r) => setDonnees(r.data))
            .catch((e) => setErreur(e.response?.data?.error || 'Lecture des formations impossible'));
    }, [departementId, anneeId]);
    const noms = coches.map((id) => (donnees?.formations || []).find((f) => f.id === id)?.nom).filter(Boolean);
    const propose = nomPropose(noms);
    const basculer = (id) => setCoches((c) => dansLOrdre(donnees, c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

    const creer = async () => {
        setEnCours(true); setErreur(null);
        try {
            const m = donnees.modeles || {};
            const nomSaisi = (nom ?? '').trim();
            // Les modèles par défaut : le premier de chaque famille, comme « Nouveau processus » de l'éditeur.
            const r = await stageAPI.creerConfiguration(departementId, anneeId, {
                formations: coches.map((id) => ({ sous_niveau_id: id })),   // E176 : la formation seule
                nom: nomSaisi && nomSaisi !== propose ? nomSaisi : null,
                date_debut: null, date_fin: null,
                modele_fiche_liaison_id: m.fiche_liaison?.[0]?.id || null, modele_visite_id: m.visite?.[0]?.id || null,
                modele_eval_id: m.eval?.[0]?.id || null, modele_documents_retour_id: m.documents_retour?.[0]?.id || null,
                etapes_config: null,
                // L2a (E170) : ses documents et ses courriels, copiés d'un autre processus — sinon des modèles d'origine.
                copier_de: copierDe ? Number(copierDe) : null
            });
            onCree(r.data.configuration?.id, r.data.dossiers_crees);
        } catch (e) {
            setErreur(e.response?.data?.error || e.message || 'La création a échoué');
            setEnCours(false);
        }
    };
    return (
        <StageModale titre="Nouveau processus de suivi" icone="fa-plus" classe="acc-np-fenetre" enCours={enCours} libelleValider="Créer et paramétrer"
                     validerDesactive={!coches.length} onValider={creer} onFermer={onFermer}>
            {!donnees && !erreur && <div><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des formations…</div>}
            {donnees && (
                <div className="acc-np">
                    {/* Des <div> et non des <label> : un label autour du menu à cases détournerait ses clics. */}
                    <div className="acc-np-l" style={{ alignItems: 'flex-start' }}><b style={{ paddingTop: 7 }}>Formations *</b>
                        <MenuFormations donnees={donnees} coches={coches} onBasculer={basculer} />
                    </div>
                    <label><b>Nom de la vignette</b>
                        <input className="input-sm" value={nom ?? propose} maxLength={200} placeholder={propose || 'cochez d\'abord une formation'}
                               onChange={(e) => setNom(e.target.value)} />
                    </label>
                    <label><b>Documents et courriels</b>
                        <select className="input-sm" value={copierDe} onChange={(e) => setCopierDe(e.target.value)}>
                            <option value="">Partir des modèles d'origine du département</option>
                            {(donnees.configurations || []).map((c) => (
                                <option key={c.id} value={c.id}>Copier ceux de « {c.titre} »</option>
                            ))}
                        </select>
                    </label>
                    <div className="acc-np-note">
                        <i className="fa-solid fa-circle-info" /> La vignette est créée <b>avec ses documents et ses courriels déjà remplis</b> (copiés),
                        et les dossiers des étudiants inscrits (stagiaires et apprentis) sont ouverts. Elle s'ouvre ensuite sur <b>« Le processus »</b> :
                        dates, étapes. Aucun courriel ne part.
                    </div>
                </div>
            )}
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
        </StageModale>
    );
}

/** « Nouveau processus de certification » : la formation et ce qui est certifié (qui devient le premier livrable). */
function NouveauCertif({ departementId, anneeId, onCree, onFermer }) {
    const [donnees, setDonnees] = useState(null);
    // E171 (fin, v86) : PLUSIEURS formations — une formation peut aussi être dans plusieurs processus de certification.
    const [coches, setCoches] = useState([]);
    const [nom, setNom] = useState('');
    const [copierDe, setCopierDe] = useState('');   // L2b (E170) : '' = ceux du département
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    React.useEffect(() => {
        stageAPI.getCertifParametrage(departementId, anneeId)
            .then((r) => setDonnees(r.data))
            .catch((e) => setErreur(e.response?.data?.error || 'Lecture des formations impossible'));
    }, [departementId, anneeId]);
    const formations = donnees?.formations || [];
    const basculer = (id) => setCoches((c) => formations.map((f) => f.id).filter((x) => (x === id ? !c.includes(id) : c.includes(x))));
    // ⚠️ Le PRÉFIXE seul (« BUT2 ») : une certification ne s'appelle pas « … — Stage » (régression E176 évitée).
    const titre = prefixeFormations(coches.map((id) => formations.find((f) => f.id === id)?.nom).filter(Boolean));
    const creer = async () => {
        setEnCours(true); setErreur(null);
        try {
            // Le serveur exige au moins un livrable : le premier porte le nom du processus (modifiable ensuite).
            const r = await stageAPI.creerProcessusCertif(departementId, anneeId, {
                formations: coches, nom: nom.trim(), delai_relance_sem: 1,
                livrables: [{ libelle: nom.trim(), obligatoire: true, formats_acceptes: 'pdf',
                              attest_lecture: true, attest_periode: true, attest_confidentialite: false,
                              hors_entreprise_possible: false, actif: true }],
                // L2b (E170) : ses courriels et ses textes, copiés d'un autre processus — sinon ceux du département.
                copier_de: copierDe ? Number(copierDe) : null
            });
            onCree(r.data.id);
        } catch (e) {
            const d = e.response?.data;
            setErreur([d?.error, d?.detail].filter(Boolean).join(' — ') || e.message || 'La création a échoué');
            setEnCours(false);
        }
    };
    return (
        <StageModale titre="Nouveau processus de certification" icone="fa-plus" classe="acc-np-fenetre" enCours={enCours} libelleValider="Créer et paramétrer"
                     validerDesactive={!coches.length || !nom.trim()} onValider={creer} onFermer={onFermer}>
            {!donnees && !erreur && <div><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des formations…</div>}
            {donnees && (
                <div className="acc-np">
                    <div className="acc-np-l" style={{ alignItems: 'flex-start' }}><b style={{ paddingTop: 7 }}>Formations *</b>
                        <MenuFormationsCertif formations={formations} coches={coches} onBasculer={basculer} />
                    </div>
                    <label><b>Ce qui est certifié *</b>
                        <input className="input-sm" value={nom} maxLength={200} placeholder="ex. Rapport et soutenance" onChange={(e) => setNom(e.target.value)} />
                    </label>
                    {titre && <div className="acc-np-nom">Vignette : <b>{titre} — {nom.trim() || '…'}</b></div>}
                    <label><b>Courriels et textes</b>
                        <select className="input-sm" value={copierDe} onChange={(e) => setCopierDe(e.target.value)}>
                            <option value="">Partir de ceux du département</option>
                            {(donnees.processus || []).map((p) => (
                                <option key={p.id} value={p.id}>Copier ceux de « {p.titre} »</option>
                            ))}
                        </select>
                    </label>
                    <div className="acc-np-note">
                        <i className="fa-solid fa-circle-info" /> « {nom.trim() || 'Rapport et soutenance'} » devient aussi le <b>premier livrable</b>
                        (modifiable). La vignette est créée <b>avec ses courriels et ses textes d'attestation</b> (copiés), puis s'ouvre sur
                        {' '}<b>« Le processus »</b> : dates, livrables. Rien n'est envoyé.
                    </div>
                </div>
            )}
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
        </StageModale>
    );
}

function StageAccueil({ donnees, departementId, anneeId, modifiable, onOuvrirSuivi, onOuvrirCertif, onOutil, onRecharger, userId = null }) {
    const [fenetre, setFenetre] = useState(null);   // 'suivi' · 'certif' · 'n1' · 'n1certif'
    const [enCours, setEnCours] = useState(false);
    const [bilan, setBilan] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [aSupprimer, setASupprimer] = useState(null);   // E177 : { type: 'suivi' | 'certif', v }
    // L1c : l'affichage retenu par compte
    const mem = React.useMemo(() => memoire(userId), [userId]);
    const [vues, setVues] = useState(() => mem.lire('vues', { suivi: 'cartes', certif: 'cartes' }));
    const [replis, setReplis] = useState(() => mem.lire('replis', { suivi: false, certif: false }));
    const [masquerTermines, setMasquerTermines] = useState(() => mem.lire('masquerTermines', true));
    const changerVue = (ligne, v) => setVues((x) => { const n = { ...x, [ligne]: v }; mem.ecrire('vues', n); return n; });
    const replier = (ligne) => setReplis((x) => { const n = { ...x, [ligne]: !x[ligne] }; mem.ecrire('replis', n); return n; });
    const basculerTermines = (v) => { setMasquerTermines(v); mem.ecrire('masquerTermines', v); };

    if (!donnees) {
        return <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture de l'accueil…</div></div>;
    }
    const { suivi, outils } = donnees;
    // « Masquer les terminés » (certification) : les terminés ne s'affichent pas et ne comptent pas pour le compact.
    const certifTermines = donnees.certif.filter((v) => v.etat === 'termine');
    const certif = masquerTermines ? donnees.certif.filter((v) => v.etat !== 'termine') : donnees.certif;

    const recupererN1 = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.recupererN1(departementId, anneeId);
            setFenetre(null);
            // Le même bilan que l'écran Paramétrage › Processus d'avant (mêmes champs de la réponse).
            setBilan({ titre: `Processus repris de ${r.data.annee_source.libelle}`, lignes: [
                `${r.data.copiees.length} processus copié(s) — sans leurs dates, qu'il reste à saisir.`,
                ...r.data.copiees.map((c) => `· ${c.formation}${c.semestre ? ` ${c.semestre}` : ''} — ${c.dossiers_crees} dossier(s) ouvert(s)`),
                ...(r.data.ignorees.length
                    ? [`${r.data.ignorees.length} ignorée(s) :`, ...r.data.ignorees.map((c) => `· ${c.formation}${c.semestre ? ` ${c.semestre}` : ''} — ${c.motifs.join(' ; ')}`)]
                    : [])
            ] });
            onRecharger();
        } catch (e) {
            setFenetre(null);
            setErreur(e.response?.data?.detail || e.response?.data?.error || 'La reprise a échoué');
        } finally { setEnCours(false); }
    };
    const reprendreCertif = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.reprendreProcessusCertifN1(departementId, anneeId);
            setFenetre(null);
            setBilan({ titre: `Processus repris de ${r.data.annee_source.libelle}`, lignes: [
                `${r.data.copies.length} processus copié(s) — sans leurs dates, qu'il reste à saisir.`,
                ...r.data.copies.map((c) => `· ${c.libelle} — ${c.livrables} livrable(s)`),
                ...(r.data.ignores.length ? [`${r.data.ignores.length} ignoré(s) :`, ...r.data.ignores.map((c) => `· ${c.libelle} — ${c.motif}`)] : [])
            ] });
            onRecharger();
        } catch (e) {
            setFenetre(null);
            setErreur([e.response?.data?.error, e.response?.data?.detail].filter(Boolean).join(' — ') || 'La reprise a échoué');
        } finally { setEnCours(false); }
    };
    const lectureSeule = modifiable ? undefined : 'Lecture seule : votre profil ne permet pas de modifier ce module.';

    // E177 — supprimer un processus dont la campagne n'est pas lancée (le serveur garde sa règle et refuse sinon).
    const supprimer = async () => {
        const { type, v } = aSupprimer;
        setEnCours(true); setErreur(null);
        try {
            if (type === 'suivi') await stageAPI.supprimerConfiguration(v.id, departementId);
            else await stageAPI.supprimerProcessusCertif(v.id, departementId);
            setASupprimer(null);
            setBilan({ titre: 'Processus supprimé', lignes: [
                type === 'suivi' && v.compteurs.dossiers
                    ? `« ${v.titre} » et ses ${v.compteurs.dossiers} dossier(s) en attente sont supprimés.`
                    : `« ${v.titre} » est supprimé.`] });
            onRecharger();
        } catch (e) {
            setASupprimer(null);
            setErreur([e.response?.data?.error, e.response?.data?.detail].filter(Boolean).join(' — ') || 'La suppression a échoué');
        } finally { setEnCours(false); }
    };

    return (
        <div className="acc">
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            {/* ── LIGNE 1 : OUTILS GÉNÉRAUX ── */}
            <div className="acc-ligne">
                <div className="acc-ligne-h"><span className="acc-ligne-titre"><i className="fa-solid fa-toolbox" style={{ color: '#475569' }} /> Outils généraux</span></div>
                <div className="acc-vignettes">
                    <Outil couleur="#7c3aed" icone="fa-handshake" titre="Alternance — registre des apprentis" onClick={() => onOutil('alternance')}
                           sous={outils.alternance ? `${outils.alternance.apprentis} apprenti${outils.alternance.apprentis > 1 ? 's' : ''} · entreprise, maître, tuteur` : 'registre des apprentis'}
                           chip={outils.alternance?.a_completer ? <><i className="fa-solid fa-pen" /> {outils.alternance.a_completer} à compléter</> : null} chipTon="afaire" />
                    <Outil couleur="#0891b2" icone="fa-chalkboard-user" titre="Affectation des tuteurs" onClick={() => onOutil('tuteurs')}
                           sous={outils.tuteurs ? `${outils.tuteurs.tuteurs} tuteur${outils.tuteurs.tuteurs > 1 ? 's' : ''} · charge moyenne ${String(outils.tuteurs.charge_moyenne).replace('.', ',')}` : 'tuteurs pédagogiques'}
                           chip={outils.tuteurs?.sans_tuteur ? <><i className="fa-solid fa-triangle-exclamation" /> {outils.tuteurs.sans_tuteur} étudiant{outils.tuteurs.sans_tuteur > 1 ? 's' : ''} sans tuteur</> : null} chipTon="afaire" />
                    <Outil couleur="#b45309" icone="fa-building" titre="Annuaire entreprises" onClick={() => onOutil('entreprises')}
                           sous={outils.entreprises ? `${outils.entreprises.entreprises} entreprise${outils.entreprises.entreprises > 1 ? 's' : ''}` : 'entreprises de stage et d\'alternance'} chip="consultation" />
                    <Outil couleur="#475569" icone="fa-gears" titre="Paramètres généraux" onClick={() => onOutil('general')}
                           sous="Équipe du stage · mes notifications" chip="réglages du département" />
                </div>
            </div>

            {/* ── LIGNE 2 : SUIVI STAGIAIRES / ALTERNANTS ── */}
            <div className={'acc-ligne' + (replis.suivi ? ' repliee' : '')}>
                <EnteteLigne titre="Suivi stagiaires / alternants" icone="fa-user-graduate" couleur="var(--stg-vert-d)" n={suivi.length}
                             vue={vues.suivi} onVue={(v) => changerVue('suivi', v)} replie={replis.suivi} onReplier={() => replier('suivi')}>
                    <div className="acc-ligne-btns">
                        <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable} title={lectureSeule || "Copier les processus de l'année précédente, sans leurs dates"}
                                onClick={() => setFenetre('n1')}><i className="fa-solid fa-rotate-left" /> Récupérer N-1</button>
                        <button type="button" className="btn btn-green btn-sm" disabled={!modifiable} title={lectureSeule}
                                onClick={() => setFenetre('suivi')}><i className="fa-solid fa-plus" /> Nouveau processus de suivi</button>
                    </div>
                </EnteteLigne>
                <div className={classeVignettes(vues.suivi, suivi.length)}>
                    {suivi.map((v) => <VignetteSuivi key={v.id} v={v} onOuvrir={onOuvrirSuivi} modifiable={modifiable}
                                                     onSupprimer={(x) => setASupprimer({ type: 'suivi', v: x })} />)}
                    {!suivi.length && <div className="acc-vide">Aucun processus de suivi pour cette année — « Nouveau processus de suivi » ou « Récupérer N-1 ».</div>}
                </div>
            </div>

            {/* ── LIGNE 3 : CERTIFICATION DES DOCUMENTS ── */}
            <div className={'acc-ligne' + (replis.certif ? ' repliee' : '')}>
                <EnteteLigne titre="Certification des documents" icone="fa-stamp" couleur="#1d4ed8" n={certif.length}
                             vue={vues.certif} onVue={(v) => changerVue('certif', v)} replie={replis.certif} onReplier={() => replier('certif')}>
                    {/* L1c : les processus terminés masqués par défaut — le compteur dit combien sont cachés. */}
                    <label className="acc-masquer" title="Un processus est terminé quand tous ses dossiers sont certifiés ou clos">
                        <input type="checkbox" checked={masquerTermines} onChange={(e) => basculerTermines(e.target.checked)} /> Masquer les terminés
                        {certifTermines.length > 0 && <span className="acc-chip">{certifTermines.length}</span>}
                    </label>
                    <div className="acc-ligne-btns">
                        <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable} title={lectureSeule || "Copier les processus de l'année précédente, sans leurs dates"}
                                onClick={() => setFenetre('n1certif')}><i className="fa-solid fa-clock-rotate-left" /> Reprendre l'année précédente</button>
                        <button type="button" className="btn btn-green btn-sm" disabled={!modifiable} title={lectureSeule}
                                onClick={() => setFenetre('certif')}><i className="fa-solid fa-plus" /> Nouveau processus de certification</button>
                    </div>
                </EnteteLigne>
                <div className={classeVignettes(vues.certif, certif.length)}>
                    {certif.map((v) => <VignetteCertif key={v.id} v={v} onOuvrir={onOuvrirCertif} modifiable={modifiable}
                                                       onSupprimer={(x) => setASupprimer({ type: 'certif', v: x })} />)}
                    {!certif.length && (
                        <div className="acc-vide">
                            {certifTermines.length ? `Tous les processus de certification sont terminés (${certifTermines.length}) — décochez « Masquer les terminés » pour les voir.`
                                : 'Aucun processus de certification pour cette année.'}
                        </div>
                    )}
                </div>
            </div>

            {fenetre === 'suivi' && (
                <NouveauSuivi departementId={departementId} anneeId={anneeId} onFermer={() => setFenetre(null)}
                              onCree={(id) => { setFenetre(null); onRecharger(); if (id) onOuvrirSuivi({ id, pret: false, nouveau: true }); }} />
            )}
            {fenetre === 'certif' && (
                <NouveauCertif departementId={departementId} anneeId={anneeId} onFermer={() => setFenetre(null)}
                               onCree={(id) => { setFenetre(null); onRecharger(); if (id) onOuvrirCertif({ id, pret: false, nouveau: true }); }} />
            )}
            {fenetre === 'n1' && (
                <StageModale titre="Reprendre les processus de l'année précédente ?" icone="fa-rotate-left" enCours={enCours}
                             libelleValider="Reprendre" onValider={recupererN1} onFermer={() => setFenetre(null)}>
                    <div>Chaque processus de suivi de l'année précédente sera recopié sur {donnees.annee?.libelle} :</div>
                    <ul>
                        <li>ses formations, ses documents et courriels et son paramétrage des étapes ;</li>
                        <li><b>ses dates ne sont pas copiées</b> — ce sont précisément celles qui changent ;</li>
                        <li>les dossiers de stage des étudiants concernés seront ouverts ;</li>
                        <li>un processus déjà présent cette année ne sera pas dupliqué.</li>
                    </ul>
                </StageModale>
            )}
            {fenetre === 'n1certif' && (
                <StageModale titre="Reprendre les processus de l'année précédente ?" icone="fa-rotate-left" enCours={enCours}
                             libelleValider="Reprendre" onValider={reprendreCertif} onFermer={() => setFenetre(null)}>
                    <div>Chaque processus de certification de l'année précédente sera recopié sur {donnees.annee?.libelle} :</div>
                    <ul>
                        <li>sa formation, son nom, son délai de relance, ses livrables et leurs attestations ;</li>
                        <li><b>ses dates ne sont pas copiées</b> — ce sont précisément celles qui changent ;</li>
                        <li>un processus du même nom déjà présent cette année n'est pas dupliqué ;</li>
                        <li>rien n'est envoyé.</li>
                    </ul>
                </StageModale>
            )}
            {aSupprimer && (
                <StageModale titre="Supprimer ce processus ?" icone="fa-trash" ton="danger" enCours={enCours}
                             libelleValider="Supprimer" onValider={supprimer} onFermer={() => setASupprimer(null)}>
                    <div style={{ marginBottom: 6 }}><b>{aSupprimer.v.titre}</b></div>
                    <ul>
                        {aSupprimer.type === 'suivi'
                            ? <li>Ses <b>{aSupprimer.v.compteurs.dossiers}</b> dossier(s), tous <b>en attente</b>, sont supprimés avec lui.</li>
                            : <li>Il n'a encore aucun dossier : il n'a jamais été lancé.</li>}
                        <li>Sa campagne n'est pas lancée : <b>aucun courriel n'est parti</b>, aucun lien n'a été donné.</li>
                        <li>Ses {aSupprimer.type === 'suivi' ? 'documents et courriels' : 'courriels et textes d\'attestation'} (sa copie) sont supprimés ; ceux du département ne bougent pas.</li>
                    </ul>
                    <div className="stg-encart stg-encart-alerte"><i className="fa-solid fa-triangle-exclamation" /><span>Cette suppression ne se défait pas.</span></div>
                </StageModale>
            )}
            {bilan && (
                <StageModale titre={bilan.titre} icone="fa-circle-check" ton="succes" onFermer={() => setBilan(null)}>
                    {bilan.lignes.map((l, i) => <div key={i} style={{ marginBottom: 4 }}>{l}</div>)}
                </StageModale>
            )}
        </div>
    );
}

export default StageAccueil;
